import { blobToBytes } from '../../core/blob-bytes';
import { EngineFailure } from '../../core/errors';

/**
 * 图像编解码抽象。
 *
 * 存在的意义有两个：
 * 1. 浏览器实现可换成 OffscreenCanvas（Worker 内）或 HTMLCanvasElement（主线程兜底）；
 * 2. 测试可以注入内存实现，避免依赖 jsdom 缺失的原生 canvas。
 */
export interface DecodedImage {
  readonly width: number;
  readonly height: number;
  readonly hasAlpha: boolean;
  /** 全量 RGBA 像素（WASM 编码与像素校验使用）。 */
  readPixels(): Promise<Uint8ClampedArray>;
  /** 等距抽样像素，用于无损校验，避免整图遍历。 */
  samplePixels(step: number): Promise<Uint8ClampedArray>;
  resize(width: number, height: number): Promise<DecodedImage>;
  close(): void;
}

export interface ImageCodec {
  readonly id: string;
  decode(bytes: Uint8Array, mime: string): Promise<DecodedImage>;
  encode(pixels: Uint8ClampedArray, width: number, height: number, mime: string, quality?: number): Promise<Uint8Array>;
  supportsEncoding(mime: string): Promise<boolean>;
}

type AnyCanvas = OffscreenCanvas | HTMLCanvasElement;
type AnyContext = OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;

const ALPHA_CAPABLE_MIME = new Set(['image/png', 'image/webp', 'image/avif', 'image/gif', 'image/svg+xml']);

function createCanvas(width: number, height: number): AnyCanvas {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(width, height);
  if (typeof document !== 'undefined') {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas;
  }
  throw new EngineFailure('ENGINE_UNAVAILABLE', '当前环境没有可用的 Canvas 实现，无法进行图片转换');
}

function get2dContext(canvas: AnyCanvas): AnyContext {
  const context = canvas.getContext('2d') as AnyContext | null;
  if (!context) throw new EngineFailure('ENGINE_UNAVAILABLE', '无法创建 2D 绘图上下文');
  return context;
}

async function canvasToBytes(canvas: AnyCanvas, mime: string, quality?: number): Promise<Uint8Array> {
  if (typeof (canvas as OffscreenCanvas).convertToBlob === 'function') {
    const blob = await (canvas as OffscreenCanvas).convertToBlob({ type: mime, quality });
    return blobToBytes(blob);
  }
  const element = canvas as HTMLCanvasElement;
  const blob = await new Promise<Blob>((resolve, reject) => {
    element.toBlob(
      (result) => (result ? resolve(result) : reject(new EngineFailure('ENCODE_FAILED', `浏览器无法编码 ${mime} 图片`))),
      mime,
      quality,
    );
  });
  return blobToBytes(blob);
}

/** JPEG 不支持透明通道：按白底合成，避免出现不可预期的黑底。 */
function compositeOnWhite(pixels: Uint8ClampedArray): Uint8ClampedArray {
  const out = new Uint8ClampedArray(pixels.length);
  for (let i = 0; i < pixels.length; i += 4) {
    const alpha = pixels[i + 3] / 255;
    out[i] = pixels[i] * alpha + 255 * (1 - alpha);
    out[i + 1] = pixels[i + 1] * alpha + 255 * (1 - alpha);
    out[i + 2] = pixels[i + 2] * alpha + 255 * (1 - alpha);
    out[i + 3] = 255;
  }
  return out;
}

class BrowserDecodedImage implements DecodedImage {
  constructor(
    private readonly bitmap: ImageBitmap,
    readonly hasAlpha: boolean,
    private readonly sourceMime: string,
  ) {}

  get width(): number {
    return this.bitmap.width;
  }

  get height(): number {
    return this.bitmap.height;
  }

  async readPixels(): Promise<Uint8ClampedArray> {
    const canvas = createCanvas(this.width, this.height);
    const context = get2dContext(canvas);
    context.drawImage(this.bitmap, 0, 0);
    return context.getImageData(0, 0, this.width, this.height).data;
  }

  async samplePixels(step: number): Promise<Uint8ClampedArray> {
    const stride = Math.max(1, Math.floor(step));
    const sampleCols: number[] = [];
    for (let x = 0; x < this.width; x += stride) sampleCols.push(x);
    const sampleRows: number[] = [];
    for (let y = 0; y < this.height; y += stride) sampleRows.push(y);
    if (sampleCols.length === 0 || sampleRows.length === 0) return new Uint8ClampedArray(0);

    const stripHeight = Math.max(1, Math.min(this.height, 64));
    const out = new Uint8ClampedArray(sampleRows.length * sampleCols.length * 4);
    let cursor = 0;

    for (let stripStart = 0; stripStart < this.height; stripStart += stripHeight) {
      const stripEnd = Math.min(this.height, stripStart + stripHeight);
      const rowsInStrip = sampleRows.filter((y) => y >= stripStart && y < stripEnd);
      if (rowsInStrip.length === 0) continue;
      const stripCanvas = createCanvas(this.width, stripEnd - stripStart);
      const context = get2dContext(stripCanvas);
      context.drawImage(
        this.bitmap,
        0,
        stripStart,
        this.width,
        stripEnd - stripStart,
        0,
        0,
        this.width,
        stripEnd - stripStart,
      );
      const data = context.getImageData(0, 0, this.width, stripEnd - stripStart).data;
      for (const y of rowsInStrip) {
        const rowOffset = (y - stripStart) * this.width;
        for (const x of sampleCols) {
          const index = (rowOffset + x) * 4;
          out[cursor] = data[index];
          out[cursor + 1] = data[index + 1];
          out[cursor + 2] = data[index + 2];
          out[cursor + 3] = data[index + 3];
          cursor += 4;
        }
      }
    }
    return out;
  }

  async resize(width: number, height: number): Promise<DecodedImage> {
    if (width === this.width && height === this.height) return this;
    const canvas = createCanvas(width, height);
    const context = get2dContext(canvas);
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(this.bitmap, 0, 0, width, height);
    const resized = await createImageBitmap(canvas as unknown as ImageBitmapSource);
    return new BrowserDecodedImage(resized, this.hasAlpha, this.sourceMime);
  }

  close(): void {
    this.bitmap.close();
  }
}

export function createBrowserCodec(): ImageCodec {
  const supportCache = new Map<string, boolean>();

  return {
    id: 'image.browser.codec',

    async decode(bytes: Uint8Array, mime: string): Promise<DecodedImage> {
      if (typeof createImageBitmap !== 'function') {
        throw new EngineFailure('ENGINE_UNAVAILABLE', '当前环境不支持 createImageBitmap，无法解码图片');
      }
      const blob = new Blob([bytes as unknown as ArrayBuffer], { type: mime || 'application/octet-stream' });
      try {
        const bitmap = await createImageBitmap(blob);
        return new BrowserDecodedImage(bitmap, ALPHA_CAPABLE_MIME.has(mime), mime);
      } catch {
        throw new EngineFailure('DECODE_FAILED', `无法解码图片（声明类型 ${mime || '未知'}），文件可能已损坏。`);
      }
    },

    async encode(pixels, width, height, mime, quality): Promise<Uint8Array> {
      if (width <= 0 || height <= 0) throw new EngineFailure('ENCODE_FAILED', '输出尺寸无效');
      const canvas = createCanvas(width, height);
      const context = get2dContext(canvas);
      const source = mime === 'image/jpeg' ? compositeOnWhite(pixels) : pixels;
      const imageData = new ImageData(source, width, height);
      context.putImageData(imageData, 0, 0);
      return canvasToBytes(canvas, mime, quality);
    },

    async supportsEncoding(mime: string): Promise<boolean> {
      const cached = supportCache.get(mime);
      if (cached !== undefined) return cached;
      try {
        const canvas = createCanvas(1, 1);
        const context = get2dContext(canvas);
        context.fillStyle = '#000';
        context.fillRect(0, 0, 1, 1);
        const bytes = await canvasToBytes(canvas, mime, 0.9);
        const supported = bytes.length > 0;
        supportCache.set(mime, supported);
        return supported;
      } catch {
        supportCache.set(mime, false);
        return false;
      }
    },
  };
}

export function mayHaveAlpha(mime: string): boolean {
  return ALPHA_CAPABLE_MIME.has(mime);
}

/** 抽样步长：把比对点数控制在 ~64×64 量级，避免大图逐像素比对。 */
export function sampleStepFor(width: number, height: number, target = 64): number {
  const longest = Math.max(width, height, 1);
  return Math.max(1, Math.ceil(longest / target));
}
